import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

import type { CopyBlobsToCompanionsResponse } from '@/services/types/blob-types';

interface CompanionCopyResultsProps {
  result: CopyBlobsToCompanionsResponse;
  onClose?: () => void;
}

export const CompanionCopyResults = ({
  result,
  onClose,
}: CompanionCopyResultsProps) => {
  if (!result.companions || result.companions.length === 0) {
    return (
      <div className="text-sm text-zinc-600">
        No companion matters found for this matter.
      </div>
    );
  }

  const totalCreated = result.companions.reduce(
    (sum, companion) =>
      sum + companion.actions.filter((a) => a.action === 'create').length,
    0,
  );

  const totalSkipped = result.companions.reduce(
    (sum, companion) =>
      sum + companion.actions.filter((a) => a.action === 'skip').length,
    0,
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex gap-4 text-sm border-b border-zinc-200 pb-3">
        <div className="flex items-center gap-1.5">
          <CheckCircleIcon className="h-4 w-4 text-green-600" />
          <span className="text-zinc-700">
            {result.dry_run ? 'Would create' : 'Created'}:{' '}
            <span className="font-semibold">{totalCreated}</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <XCircleIcon className="h-4 w-4 text-zinc-400" />
          <span className="text-zinc-700">
            {result.dry_run ? 'Would skip' : 'Skipped'}:{' '}
            <span className="font-semibold">{totalSkipped}</span>
          </span>
        </div>
      </div>

      {/* Per-Companion Details */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {result.companions.map((companion) => {
          const created = companion.actions.filter(
            (a) => a.action === 'create',
          ).length;
          const skipped = companion.actions.filter(
            (a) => a.action === 'skip',
          ).length;

          return (
            <div
              key={companion.companion_matter_id}
              className="border border-zinc-200 rounded-lg p-3"
            >
              {/* Companion Matter Name */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm text-zinc-900">
                  {companion.companion_matter_name}
                </h4>
                <div className="flex gap-3 text-xs text-zinc-600">
                  <span className="text-green-600">{created} created</span>
                  <span className="text-zinc-400">{skipped} skipped</span>
                </div>
              </div>

              {/* Actions List */}
              <div className="space-y-1.5">
                {companion.actions.map((action, idx) => (
                  <div
                    key={`${action.blob_id}-${idx}`}
                    className="flex items-start gap-2 text-xs"
                  >
                    {action.action === 'create' ? (
                      <CheckCircleIcon className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircleIcon className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-900 truncate">
                        {action.blob_name}
                      </p>
                      <p className="text-zinc-500">
                        {action.action === 'create' ? (
                          <>
                            {result.dry_run ? 'Would be copied' : 'Copied'} to{' '}
                            <span className="font-medium">
                              {action.target_collection_name}
                            </span>
                          </>
                        ) : (
                          // eslint-disable-next-line react/jsx-no-useless-fragment
                          <>
                            {action.reason === 'hash_exists'
                              ? 'Already exists (same file hash)'
                              : 'Skipped'}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {result.dry_run && (
        <div className="mt-3 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
          This is a preview. No files have been copied yet.
        </div>
      )}
    </div>
  );
};
